import { useLayoutEffect, useRef } from "react";
import { Box, Card, Heading, Input, Text, Tooltip } from "@chakra-ui/react";
import { ExtractionDataItem, TemplateField } from "../../../api/types";
import { useUpdateExtractionField } from "../../../api/useUpdateExtractionField";
import { typedSxMap } from "../../../utils/typedSxMap";
import "./../style/TableStyle.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FaCircleCheck, FaRegCircle, FaTableList } from "react-icons/fa6";
import { MdError } from "react-icons/md";

export const DRAWER_CLASS_TOP_REFERENCE = "pipeline-importer-header-divider";

type ReviewRowDetailPDFProps = {
  containerRef?: any;
  extractionId?: string;
  isReadOnly?: boolean;
  template: TemplateField[];
  data: { [key: string]: ExtractionDataItem };
  selectedDataItem?: ExtractionDataItem | null;
  selectedTemplateKey?: string | null;
  leftAdjustment: string;
  topAdjustment: string;
  setSelectedDataItem: (item: ExtractionDataItem | null) => void;
  setSelectedTemplateKey: (templateKey: string | null) => void;
  onScroll: () => void;
  onUpdateDataItem?: (item: ExtractionDataItem, key: string) => void;
};

const styles = typedSxMap({
  container: {
    flex: 1,
    minWidth: "500px",
    position: "sticky",
    top: 0,
    height: "calc(100vh - 94px)",
    display: "flex",
  },
  cardHeader: {
    padding: "0 0 var(--m) 0",
    margin: 0,
    marginX: "var(--m)",
  },
  cardTitle: {
    fontSize: "24px",
    padding: 0,
    margin: 0,
  },
  schemaItem: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
    padding: "0 var(--m-xs)",
    borderRadius: "6px",
    zIndex: 1,

    "&:last-child": {
      borderBottom: "none",
    },
    "& p": {
      marginBottom: 0,
    },
    "&:hover": {
      backgroundColor: "var(--color-background-menu-hover)",
      boxShadow: "0 -2px 0 var(--color-background-menu-hover)",

      "& .cardItemExtracted": {
        borderBottomColor: "transparent !important",
      },
    },
  },
  selectedSchemaItem: {
    top: "-24px",
    bottom: "-24px",
    position: "sticky",
    backgroundColor: "var(--color-background-menu-hover)",
    zIndex: 10,
    boxShadow: "0 -2px 0 var(--color-background-menu-hover)",
    "& .cardItemExtracted": {
      borderBottomColor: "transparent !important",
    },
  },
  cardItemExtracted: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: "4px 0px",

    "& p": {
      marginBottom: 0,
    },
  },
  cardItemWrapper: {
    display: "flex",
    gap: "60px",
    marginLeft: "var(--m-xs)",
    width: "calc(100% - var(--m-xs))",
    position: "relative",
  },
  label: {
    fontSize: "var(--font-size-xs, 12px)",
    fontWeight: 400,
    minWidth: "40%",
    color: "var(--color-text-soft)",
  },
  value: {
    fontSize: "var(--font-size-sm, 14px)",
    fontWeight: 400,
    minHeight: "21px",
  },
  dataInput: {
    marginLeft: "0px",
    padding: "2px 0px",
    height: "21px",
    border: "none",
    fontSize: "var(--font-size-sm, 14px)",
    "&:disabled": {
      opacity: 1,
      color: "inherit",
    },
    "&:hover, &:focus": {
      boxShadow: "inset 0 0 0 1px var(--color-border)",
      marginLeft: "-2px",
      paddingLeft: "2px",
    },
  },
  cardBody: {
    alignItems: "flex-start",
    gap: "0",
    flexDirection: "column",
    borderRadius: "var(--radius-xl, 12px)",
    border: "1px solid var(--color-border)",
    backgroundColor: "transparent",
    padding: "var(--m) var(--m-xs)",
    height: "100%",
    overflowY: "auto",
  },
  contentBody: {
    flex: 1,
    padding: "0 var(--m)",
    paddingRight: "var(--m-xs)",
  },
  overlay: {
    "& .chakra-modal__content-container": {
      width: "auto",
    },
  },
  dataIcon: {
    marginRight: "4px",
    width: "20px",
  },
  closeButton: {
    backgroundColor: "var(--color-side-button-primary)",
    position: "absolute",
    right: "100%",
    borderRadius: "var(--radius-md, 8px) 0px 0px var(--radius-md, 8px)",
    maxWidth: "40px",
    height: "40px",
    minWidth: "40px",
    "&:hover": {
      backgroundColor: "var(--color-side-button-primary-hover)",
    },
    "&:focus-visible": {
      boxShadow: "none",
    },
  },
  tooltip: {
    position: "absolute",
    backgroundColor: "var(--color-background-modal)",
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius)",
    marginTop: "var(--m-xs)",
    boxShadow: "0 0 0 1px var(--color-border), 0 5px 15px rgba(0, 0, 0, 0.2)",
    maxWidth: "300px",
    zIndex: 10000,
    color: "var(--color-text)",
  },
  tableDataItem: {
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
  },
  tableIcon: {
    marginRight: "8px",
    color: "var(--color-text-soft)",
    fontSize: "18px",
  },
  sectionHeading: {
    paddingLeft: "var(--m-xs)",
    marginBottom: "var(--m-xxxs)",
  },
});

const getDataIcon = (item: ExtractionDataItem) => {
  if (item?.table_data) {
    const hasErrors = item.table_data.some((row) => Object.values(row).some((cell) => cell.errors && cell.errors.length > 0));
    return hasErrors ? <MdError color="var(--color-cell-error)" size={21} /> : <FaCircleCheck color="green" size={18} />;
  }
  if (item?.errors && item?.errors.length > 0) {
    return (
      <Box style={{ width: 15 }}>
        <MdError color={"var(--color-cell-error)"} size={21} style={{ marginLeft: "-1px" }} />
      </Box>
    );
  } else if (item?.value) {
    return <FaCircleCheck color="green" size={18} />;
  } else {
    return <FaRegCircle color="gray" size={18} />;
  }
};

export default function ReviewRowDetailPDF({
  extractionId,
  isReadOnly,
  template,
  data,
  selectedDataItem,
  selectedTemplateKey,
  leftAdjustment,
  topAdjustment,
  setSelectedDataItem,
  setSelectedTemplateKey,
  onScroll,
  onUpdateDataItem,
}: ReviewRowDetailPDFProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inputsRef = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const isDirtyField = useRef<{ [key: string]: boolean | null }>({});

  const updateExtractionField = useUpdateExtractionField();

  const editCell = async ({ key, value }: { key: string; value?: string }) => {
    if (!isDirtyField.current[key] || !extractionId) return;
    try {
      const updatedItem = await updateExtractionField.mutateAsync({ extractionId, key, value: value ?? "" });
      isDirtyField.current = { ...isDirtyField.current, [key]: false };
      onUpdateDataItem && onUpdateDataItem(updatedItem, key);
    } catch (error) {
      console.error("Failed to update extraction field:", error);
    }
  };

  useLayoutEffect(() => {
    ref.current?.parentElement?.focus();
  });
  const fields = template.sort((item1, item2) => {
    // Prioritize table fields
    if (item1.data_type === "table" && item2.data_type !== "table") {
      return -1;
    } else if (item1.data_type !== "table" && item2.data_type === "table") {
      return 1;
    }

    // Then sort by value presence
    const value1 = data[item1.key]?.value;
    const value2 = data[item2.key]?.value;

    if (value1 && !value2) {
      return -1;
    } else if (!value1 && value2) {
      return 1;
    } else {
      return 0;
    }
  });

  const tableFields = fields.filter((field) => field.data_type === "table");
  const nonTableFields = fields.filter((field) => field.data_type !== "table");

  return (
    <Box sx={styles.container} className="reviewRowDetail">
      <Box sx={styles.contentBody}>
        <Card variant="outline" sx={styles.cardBody} onScroll={onScroll}>
          {tableFields.length > 0 && (
            <>
              <Heading as="h3" size="md" sx={styles.sectionHeading}>
                Tables
              </Heading>
              {tableFields.map(({ key: originalKey, name, data_type }, index) => {
                const item = data[originalKey];
                const key = originalKey || name || "";
                const value = item?.value;

                const id = `details-${item?.page_element?.id}-${originalKey}`;
                if (selectedTemplateKey === originalKey && item?.page_element?.id === selectedDataItem?.page_element?.id) {
                  inputsRef?.current && inputsRef.current[originalKey]?.focus();
                }
                return (
                  <Box
                    sx={{
                      ...styles.schemaItem,
                      ...(selectedDataItem?.page_element?.id === item?.page_element?.id && selectedTemplateKey === originalKey
                        ? styles.selectedSchemaItem
                        : {}),
                    }}
                    key={key + item?.page_element?.id + name + value + index}
                    onClick={() => {
                      item && setSelectedDataItem(item);
                      setSelectedTemplateKey(originalKey);
                    }}>
                    <Box sx={styles.dataIcon}>{getDataIcon(item)}</Box>
                    <Box sx={styles.cardItemWrapper}>
                      <Box id={id} display="block" height="100%" position="absolute" top={topAdjustment} left={leftAdjustment}></Box>
                      <Box sx={styles.cardItemExtracted}>
                        <Box sx={styles.tableDataItem}>
                          <Box as={FaTableList} sx={styles.tableIcon} />
                          <Text sx={styles.value}>{name || key}</Text>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}

          <Heading
            as="h3"
            size="md"
            sx={{
              ...styles.sectionHeading,
              marginTop: tableFields.length > 0 ? "var(--m-xs)" : 0,
            }}>
            Fields
          </Heading>
          {nonTableFields.map(({ key: originalKey, name, validations }, index) => {
            const item = data[originalKey];
            const key = originalKey || name || "";
            const errors = item?.errors ? validations.find(({ id }) => item.errors[0] === id) : null;
            let value = item?.value;

            const id = `details-${item?.page_element?.id}-${originalKey}`;
            if (selectedTemplateKey === originalKey && item?.page_element?.id === selectedDataItem?.page_element?.id) {
              inputsRef?.current && inputsRef.current[originalKey]?.focus();
            }
            return (
              <Box
                sx={{
                  ...styles.schemaItem,
                  ...(selectedDataItem?.page_element?.id === item?.page_element?.id && selectedTemplateKey === originalKey
                    ? styles.selectedSchemaItem
                    : {}),
                }}
                key={key + item?.page_element?.id + name + value + index}
                onClick={() => {
                  item && setSelectedDataItem(item);
                  setSelectedTemplateKey(originalKey);
                }}>
                {errors ? (
                  <Tooltip
                    sx={styles.tooltip}
                    label={errors.message}
                    aria-label={errors.message}
                    placement="top"
                    arrowShadowColor="var(--color-border)">
                    <span>
                      <Box sx={styles.dataIcon}>{getDataIcon(item)}</Box>
                    </span>
                  </Tooltip>
                ) : item?.value ? (
                  <Box sx={styles.dataIcon}>{getDataIcon(item)}</Box>
                ) : (
                  <Box sx={styles.dataIcon}>{getDataIcon(item)}</Box>
                )}
                <Box sx={styles.cardItemWrapper}>
                  <Box id={id} display="block" height="100%" position="absolute" top={topAdjustment} left={leftAdjustment}></Box>
                  <Box
                    sx={styles.cardItemExtracted}
                    style={{
                      borderBottom: name || key ? "1px solid var(--color-border)" : "none",
                    }}
                    className="cardItemExtracted">
                    <Text sx={styles.label}>{name || key}</Text>
                    <Input
                      ref={(el) => (inputsRef.current[key] = el)}
                      sx={{
                        ...styles.dataInput,
                        cursor: isReadOnly ? "pointer" : "text",
                        pointerEvents: isReadOnly ? "none" : "auto",
                        "&::placeholder": {
                          fontStyle: isReadOnly ? "italic" : "normal",
                        },
                      }}
                      autoComplete="off"
                      placeholder={isReadOnly ? "No value" : "+ Add value"}
                      defaultValue={value}
                      disabled={isReadOnly}
                      onClick={(e) => {
                        if (isReadOnly) {
                          e.stopPropagation();
                          item && setSelectedDataItem(item);
                          setSelectedTemplateKey(originalKey);
                        }
                      }}
                      onBlur={(e: any) => {
                        setSelectedDataItem(null);
                        editCell({
                          key: key,
                          value: e.target?.value,
                        });
                      }}
                      onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                          editCell({
                            key: key,
                            value: e.target?.value,
                          });
                        }
                      }}
                      onChange={(e: any) => {
                        isDirtyField.current = { ...isDirtyField.current, [key]: true };
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Card>
      </Box>
    </Box>
  );
}
